import styles from './FileUploader.module.css';

import DeleteIcon from 'assets/svg/delete-01.svg';
import ClipIcon from 'assets/svg/screpka.svg';

import React, { memo, useCallback, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useDrop } from 'react-dnd';
import { NativeTypes } from 'react-dnd-html5-backend';
import classNames from 'classnames';
import IconButton from '../IconButton';
import RegularButton from '../RegularButton';
import Icon from '../../helpers/components/Icon';

/**
 * Компонент `FileUploader` - универсальный загрузчик файлов с поддержкой Drag-and-Drop.
 *
 * Компонент позволяет:
 * - Загружать файлы через диалог выбора или перетаскиванием
 * - Отображать предварительно загруженные файлы (включая URL)
 * - Настраивать расположение списка файлов
 * - Ограничивать типы файлов
 * - Удалять файлы из списка
 *
 * @param {Object} props - Свойства компонента
 * @param {Array<string|File>} [props.initialFiles=[]] - Начальный список файлов
 * @param {Function} [props.onFilesChange] - Колбэк при изменении списка файлов
 * @param {Boolean} [props.allowAllFiles=true] - Разрешены ли все типы файлов
 * @param {String} [props.accept='*\/*'] - MIME-типы для input (работает если allowAllFiles=false)
 * @param {Function} [props.onFileRemove] - Колбэк при удалении файла
 * @param {String} [props.listPosition='bottom'] - Позиция списка файлов ('top', 'bottom')
 * @param {String} [props.placeholder='Перетащите файл сюда или'] - Текст в зоне загрузки
 * @param {String} [props.buttonText='Загрузить файл'] - Текст кнопки загрузки
 * @param {Number} [props.maxFiles=Infinity] - Максимальное количество файлов
 * @param {Number} [props.maxFileSize=Infinity] - Максимальный размер файла в байтах
 * @param {Function} [props.onError] - Колбэк при ошибках (превышении лимитов и т.д.)
 * @param {Boolean} [props.disabled=false] - Отключает возможность загрузки/удаления
 * @param {String} [props.description] - Дополнительное описание или инструкция под зоной загрузки
 * @param {Boolean} [props.multiple=true] - Разрешены ли множественный выбор файлов
 * @param {Function} [props.renderFilePreview] - Кастомное отображение загруженного файла.
 *   Функция получает три параметра:
 *   - file: объект File или строка с URL
 *   - index: индекс файла в массиве
 *   - handleRemove: функция для удаления файла
 * @param {Object} [props.customStyles={}] - Объект с классами и стилями для кастомизации компонента.
 *   Поддерживаются следующие свойства:
 *   - `mainContainer` (string): CSS-класс для основного контейнера
 *   - `fileList` (string): CSS-класс для списка файлов
 *   - `fileListStyle` (Object): Инлайн-стили для списка файлов (например, { gap: '20px' })
 *   - `fileItem` (string): CSS-класс для элемента файла в списке
 *   - `fileItemStyle` (Object): Инлайн-стили для элемента файла
 *   - `fileName` (string): CSS-класс для имени файла
 *   - `deleteButton` (string): CSS-класс для кнопки удаления файла
 *   - `dropZone` (string): CSS-класс для зоны переноса файлов
 *   - `title` (string): CSS-класс для контейнера заголовка в зоне загрузки
 *   - `titleText` (string): CSS-класс для текста в зоне загрузки
 *   - `uploadButton` (string): CSS-класс для кнопки загрузки файлов
 *   - `description` (string): CSS-класс для блока с описанием
 *   - `icon` (string): CSS-класс для иконки
 *
 * @returns {React.ReactElement} Компонент загрузки файлов
 *
 * @example
 * // Базовое использование
 * <FileUploader
 *   onFilesChange={(files) => console.log('Files changed:', files)}
 * />
 *
 * @example
 * // С предзагруженными файлами и ограничением типов
 * <FileUploader
 *   initialFiles={['https://example.com/image.jpg', existingFile]}
 *   allowAllFiles={false}
 *   accept="image\/*"
 *   onFilesChange={handleFilesChange}
 * />
 *
 * @example
 * // С настройкой отображения списка и ограничениями
 * <FileUploader
 *   listPosition="bottom"
 *   maxFiles={5}
 *   maxFileSize={5 * 1024 * 1024} // 5 МБ
 *   onError={(error) => console.error(error)}
 *   description="Поддерживаются файлы JPG, PNG, PDF размером до 5 МБ"
 *   customStyles={{
 *     mainContainer: 'custom-container',
 *     dropZone: 'custom-dropzone',
 *     fileItem: 'custom-file-item'
 *   }}
 * />
 *
 * @example
 * // С кастомным отображением предпросмотра файлов
 * <FileUploader
 *   renderFilePreview={(file, index, handleRemove) => (
 *     <div key={index} className="custom-preview">
 *       {typeof file === 'string'
 *         ? <img src={file} alt="Preview" width="50" />
 *         : file.type.startsWith('image\/')
 *           ? <img src={URL.createObjectURL(file)} alt="Preview" width="50" />
 *           : <span>{file.name}</span>
 *       }
 *       <button onClick={() => handleRemove(index)}>Удалить</button>
 *     </div>
 *   )}
 * />
 */
const FileUploader = memo(
  ({
    initialFiles = [],
    onFilesChange,
    allowAllFiles = true,
    accept = '*/*',
    onFileRemove,
    listPosition = 'bottom',
    placeholder = 'Перетащите файл сюда или',
    buttonText = 'Загрузить файл',
    maxFiles = Infinity,
    maxFileSize = Infinity,
    onError,
    disabled = false,
    description,
    renderFilePreview,
    customStyles = {},
    multiple = true,
  }) => {
    const [files, setFiles] = useState(initialFiles);
    const inputRef = useRef(null);

    // Проверка файлов перед добавлением
    const validateFiles = useCallback(
      (filesToValidate) => {
        if (!Array.isArray(filesToValidate)) return [];

        // Проверяем максимальное количество
        if (files.length + filesToValidate.length > maxFiles) {
          const error = `Превышено максимальное количество файлов (${maxFiles})`;
          onError?.(error);
          return filesToValidate.slice(0, Math.max(0, maxFiles - files.length));
        }

        // Собираем имена существующих файлов
        const existingNames = new Set(
          files.map((file) =>
            typeof file === 'string' ? file.split('/').pop() || file : file.name,
          ),
        );

        return filesToValidate.filter((file) => {
          const fileName = typeof file === 'string' ? file.split('/').pop() || file : file.name;
          if (typeof file === 'string') return true;

          // Проверяем размер файлов
          if (file.size > maxFileSize) {
            const fileSizeMB = (maxFileSize / (1024 * 1024)).toFixed(2);
            const error = `Файл "${file.name}" превышает максимальный размер (${fileSizeMB} МБ)`;
            onError?.(error);
            return false;
          }

          // Проверка уникальности
          if (existingNames.has(fileName)) {
            onError?.(`Файл "${file.name}" уже существует в списке.`);
            return false;
          }
          return true;
        });
      },
      [files, maxFiles, maxFileSize, onError],
    );

    // Обновление массива файлов
    const handleAddFiles = useCallback(
      (newFiles) => {
        if (disabled) return;

        const filesArray = Array.isArray(newFiles) ? newFiles : [newFiles];
        const validatedFiles = validateFiles(filesArray);

        if (validatedFiles.length === 0) return;

        setFiles((prevFiles) => {
          const updatedFiles = [...prevFiles, ...validatedFiles];
          onFilesChange?.(updatedFiles);
          return updatedFiles;
        });
      },
      [disabled, validateFiles, onFilesChange],
    );

    // Drag-and-Drop обработка
    const [{ isOver, canDrop }, dropRef] = useDrop({
      accept: [NativeTypes.FILE],
      drop: ({ files: droppedFiles }) => {
        handleAddFiles(droppedFiles);
      },
      canDrop: () => !disabled,
      collect: (monitor) => ({
        isOver: monitor.isOver(),
        canDrop: monitor.canDrop(),
      }),
    });

    // Удаление файла из списка
    const handleRemoveFile = useCallback(
      (index) => {
        if (disabled) return;

        setFiles((prevFiles) => {
          const updatedFiles = [...prevFiles];
          updatedFiles.splice(index, 1);
          onFilesChange?.(updatedFiles);
          onFileRemove?.(index);
          return updatedFiles;
        });
      },
      [disabled, onFilesChange, onFileRemove],
    );

    // Выбор файла через input
    const handleSelectFile = useCallback(
      (event) => {
        const selectedFiles = Array.from(event.target.files);
        handleAddFiles(selectedFiles);
        if (inputRef.current) inputRef.current.value = '';
      },
      [handleAddFiles],
    );

    // Обработчик клика на кнопку загрузки
    const handleUploadButtonClick = useCallback(() => {
      if (disabled) return;
      inputRef.current?.click();
    }, [disabled]);

    // Стандартный рендер файла
    const defaultRenderFile = useCallback(
      (file, index) => {
        if (typeof file === 'string') {
          // Для строк (URL)
          return (
            <div
              key={index}
              className={classNames(styles.fileItem, customStyles.fileItem)}
              style={customStyles.fileItemStyle}
            >
              <span className={classNames(styles.fileName, customStyles.fileName)}>
                {file.split('/').pop() || file}
              </span>
              <IconButton
                icon={DeleteIcon}
                size={20}
                onClick={() => handleRemoveFile(index)}
                className={customStyles.deleteButton}
                disabled={disabled}
              />
            </div>
          );
        }

        // Для объектов File
        return (
          <div
            key={index}
            className={classNames(styles.fileItem, customStyles.fileItem)}
            style={customStyles.fileItemStyle}
          >
            <span className={classNames(styles.fileName, customStyles.fileName)}>{file.name}</span>
            <IconButton
              icon={DeleteIcon}
              size={20}
              onClick={() => handleRemoveFile(index)}
              className={customStyles.deleteButton}
              disabled={disabled}
            />
          </div>
        );
      },
      [handleRemoveFile, customStyles, disabled],
    );

    // Рендер списка файлов
    const renderFileList = useCallback(() => {
      if (!files?.length) return null;

      return (
        <div
          className={classNames(styles.fileList, customStyles.fileList)}
          style={customStyles.fileListStyle}
        >
          {renderFilePreview
            ? files.map((file, index) => renderFilePreview(file, index, handleRemoveFile))
            : files.map((file, index) => defaultRenderFile(file, index))}
        </div>
      );
    }, [files, defaultRenderFile, renderFilePreview, handleRemoveFile, customStyles]);

    return (
      <div
        className={classNames(styles.mainContainer, customStyles.mainContainer, {
          [styles.disabled]: disabled,
        })}
      >
        {/* Список файлов в зависимости от расположения */}
        {listPosition === 'top' && renderFileList()}

        <div
          className={classNames(
            styles.container,
            { [styles.dropZoneActive]: isOver && canDrop && !disabled },
            { [styles.disabled]: disabled },
            customStyles.dropZone,
          )}
          ref={dropRef}
        >
          {/* Зона загрузки */}
          <div className={classNames(styles.title, customStyles.title)}>
            <Icon src={ClipIcon} className={classNames(styles.icon, customStyles.icon)} />
            <span className={classNames(styles.titleText, customStyles.titleText)}>
              {placeholder}
            </span>
            <RegularButton
              variant={'outlined'}
              color={'secondary'}
              onClick={handleUploadButtonClick}
              className={classNames(styles.uploadButton, customStyles.uploadButton, {
                [styles.disabled]: disabled,
              })}
              text={buttonText}
              disabled={disabled}
            />
          </div>

          {/* Описание */}
          {description && (
            <div className={classNames(styles.description, customStyles.description)}>
              {description}
            </div>
          )}

          {/* Скрытый input */}
          <input
            ref={inputRef}
            type="file"
            multiple={multiple}
            accept={allowAllFiles ? '*/*' : accept}
            style={{ display: 'none' }}
            onChange={handleSelectFile}
            disabled={disabled}
          />
        </div>

        {/* Список файлов снизу */}
        {listPosition === 'bottom' && renderFileList()}
      </div>
    );
  },
);

FileUploader.displayName = 'FileUploader';

FileUploader.propTypes = {
  initialFiles: PropTypes.array,
  onFilesChange: PropTypes.func,
  allowAllFiles: PropTypes.bool,
  accept: PropTypes.string,
  onFileRemove: PropTypes.func,
  listPosition: PropTypes.oneOf(['top', 'bottom']),
  placeholder: PropTypes.string,
  buttonText: PropTypes.string,
  maxFiles: PropTypes.number,
  maxFileSize: PropTypes.number,
  onError: PropTypes.func,
  disabled: PropTypes.bool,
  description: PropTypes.string,
  renderFilePreview: PropTypes.func,
  multiple: PropTypes.bool,
  customStyles: PropTypes.shape({
    mainContainer: PropTypes.string,
    fileList: PropTypes.string,
    fileListStyle: PropTypes.object,
    fileItem: PropTypes.string,
    fileItemStyle: PropTypes.object,
    fileName: PropTypes.string,
    deleteButton: PropTypes.string,
    dropZone: PropTypes.string,
    title: PropTypes.string,
    titleText: PropTypes.string,
    uploadButton: PropTypes.string,
    description: PropTypes.string,
    icon: PropTypes.string,
  }),
};

export default FileUploader;